import { memo } from 'react';
import { LoadingType } from './PageLoader';
import { cn } from '@/lib/utils';

interface SuspenseLoaderProps {
  type?: LoadingType;
  className?: string;
  height?: string;
  message?: string;
}

const SuspenseLoader = memo<SuspenseLoaderProps>(({
  type = 'skeleton',
  className,
  height = 'h-96',
  message = 'Loading component...'
}) => {
  const baseClass = cn('w-full', className);

  switch (type) {
    case 'card-grid':
      return (
        <div className={baseClass}>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 bg-muted/30 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      );

    case 'table':
      return (
        <div className={cn('space-y-4', baseClass)}>
          <div className="h-12 bg-muted/30 rounded-lg animate-pulse" />
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-muted/30 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      );

    case 'minimal':
      return (
        <div className={cn('flex items-center justify-center py-4', baseClass)}>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin" />
            <span className="text-muted-foreground text-sm">{message}</span>
          </div>
        </div>
      );

    case 'spinner':
      return (
        <div className={cn('flex items-center justify-center', height, baseClass)}>
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-4 border-t-transparent border-primary rounded-full animate-spin" />
            <span className="text-muted-foreground text-sm">{message}</span>
          </div>
        </div>
      );

    case 'skeleton':
    default:
      return (
        <div className={cn(height, 'bg-muted/30 rounded-lg animate-pulse', baseClass)} />
      );
  }
});

SuspenseLoader.displayName = 'SuspenseLoader';

export default SuspenseLoader;